/**
 * Avatar Cache Utility
 *
 * Uses the browser Cache API to persist GLB avatar models across page visits.
 * This significantly reduces load times on subsequent visits by avoiding
 * re-downloading large 3D model files.
 */

const CACHE_NAME = 'avatar-cache-v1';

// File types to cache
const CACHEABLE_EXTENSIONS = ['.glb', '.fbx'];

/**
 * Check if the Cache API is available
 */
export function isCacheAvailable(): boolean {
  return 'caches' in window;
}

/**
 * Check if a URL should be cached
 */
function isCacheable(url: string): boolean {
  return CACHEABLE_EXTENSIONS.some(ext => url.toLowerCase().endsWith(ext));
}

/**
 * Get a cached response for a URL
 */
export async function getCachedAvatar(url: string): Promise<Response | null> {
  if (!isCacheAvailable() || !isCacheable(url)) {
    return null;
  }

  try {
    const cache = await caches.open(CACHE_NAME);
    const cachedResponse = await cache.match(url);

    if (cachedResponse) {
      console.log('[AvatarCache] Cache hit:', url);
      return cachedResponse;
    }

    return null;
  } catch (error) {
    console.warn('[AvatarCache] Error reading cache:', error);
    return null;
  }
}

/**
 * Store a response in the cache
 */
export async function cacheAvatar(url: string, response: Response): Promise<void> {
  if (!isCacheAvailable() || !isCacheable(url)) {
    return;
  }

  try {
    const cache = await caches.open(CACHE_NAME);
    // Clone the response since it can only be consumed once
    await cache.put(url, response.clone());
    console.log('[AvatarCache] Cached:', url);
  } catch (error) {
    console.warn('[AvatarCache] Error storing in cache:', error);
  }
}

/**
 * Fetch a URL with caching - checks cache first, then fetches and caches if needed
 */
export async function fetchWithCache(url: string): Promise<Response> {
  // Try cache first
  const cachedResponse = await getCachedAvatar(url);
  if (cachedResponse) {
    return cachedResponse;
  }

  // Fetch from network
  console.log('[AvatarCache] Cache miss, fetching:', url);
  const response = await fetch(url);

  // Cache the response if successful
  if (response.ok) {
    await cacheAvatar(url, response);
  }

  return response;
}

/**
 * Preload an avatar into the cache without blocking
 */
export async function preloadAvatarToCache(url: string): Promise<void> {
  if (!isCacheAvailable() || !isCacheable(url)) {
    return;
  }

  try {
    // Check if already cached
    const cached = await getCachedAvatar(url);
    if (cached) {
      console.log('[AvatarCache] Already cached:', url);
      return;
    }

    // Fetch and cache
    const response = await fetch(url);
    if (response.ok) {
      await cacheAvatar(url, response);
    }
  } catch (error) {
    console.warn('[AvatarCache] Preload failed:', error);
  }
}

/**
 * Preload multiple avatars in parallel
 */
export async function preloadAvatarsToCache(urls: string[]): Promise<void> {
  await Promise.all(urls.map(url => preloadAvatarToCache(url)));
}

/**
 * Clear the avatar cache
 */
export async function clearAvatarCache(): Promise<void> {
  if (!isCacheAvailable()) {
    return;
  }

  try {
    await caches.delete(CACHE_NAME);
    console.log('[AvatarCache] Cache cleared');
  } catch (error) {
    console.warn('[AvatarCache] Error clearing cache:', error);
  }
}

/**
 * Get approximate cache size (number of entries)
 */
export async function getCacheSize(): Promise<number> {
  if (!isCacheAvailable()) {
    return 0;
  }

  try {
    const cache = await caches.open(CACHE_NAME);
    const keys = await cache.keys();
    return keys.length;
  } catch (error) {
    return 0;
  }
}
